import { useShopI18n } from '../../i18n'
import type { CheckoutShippingMethod, CheckoutShippingMethodCode } from '../types'

type Props = {
  methods: CheckoutShippingMethod[]
  selectedMethod: CheckoutShippingMethodCode
  disabled: boolean
  formatCurrency: (value: number) => string
  onSelectMethod: (value: CheckoutShippingMethodCode) => void
  onSubmit: () => void
  onBack: () => void
}

const resolveEstimateKey = (code: CheckoutShippingMethodCode) =>
  code === 'express' ? 'checkout.shippingMethod.express.estimate' : 'checkout.shippingMethod.standard.estimate'

export const ShippingMethodStep = ({
  methods,
  selectedMethod,
  disabled,
  formatCurrency,
  onSelectMethod,
  onSubmit,
  onBack,
}: Props) => {
  const { t } = useShopI18n()

  return (
    <section className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
      <header>
        <h2 className="text-xl font-semibold text-slate-900">{t('checkout.shippingMethod.title')}</h2>
        <p className="mt-1 text-sm text-slate-600">{t('checkout.shippingMethod.description')}</p>
      </header>

      <div className="space-y-3">
        {methods.map((method) => {
          const isSelected = selectedMethod === method.code
          return (
            <label
              key={method.code}
              className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition ${
                isSelected ? 'border-slate-900 bg-slate-50' : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
            >
              <input
                type="radio"
                name="shipping-method"
                checked={isSelected}
                disabled={disabled}
                onChange={() => onSelectMethod(method.code)}
              />
              <div className="flex-1">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-slate-900">{method.label}</p>
                      {method.code === 'standard' ? (
                        <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                          {t('checkout.shippingMethod.recommended')}
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-1 text-xs text-slate-600">{method.description}</p>
                    <p className="mt-1 text-xs font-medium text-slate-700">{t(resolveEstimateKey(method.code))}</p>
                  </div>
                  <p className="text-sm font-semibold text-slate-900">{formatCurrency(method.amount)}</p>
                </div>
              </div>
            </label>
          )
        })}
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
          onClick={onBack}
        >
          {t('checkout.cta.back')}
        </button>
        <button
          type="button"
          className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={disabled}
          onClick={onSubmit}
        >
          {t('checkout.cta.saveMethod')}
        </button>
      </div>
    </section>
  )
}
